/**
 * "Close settings" — returns to the place the user came from.
 *
 * The settings screens are reached from the board, the workspace overview,
 * the command palette or a direct link. Closing them takes the user back to
 * that place instead of a hard-coded default, so the context is not lost.
 */

import { X } from 'lucide-react'
import { useRouter } from '@tanstack/react-router'
import { getSettingsOrigin } from '@/features/settings/settingsOrigin'

export interface CloseSettingsButtonProps {
  /** Where to go when the user opened the settings directly (no origin). */
  fallback: string
}

export function CloseSettingsButton({ fallback }: CloseSettingsButtonProps) {
  const router = useRouter()

  return (
    <button
      type="button"
      className="text-surface-600 hover:bg-surface-100 dark:text-surface-300 dark:hover:bg-surface-800 inline-flex min-h-11 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-medium"
      onClick={() => {
        const origin = getSettingsOrigin()
        // `router.navigate` (not `router.history.push`) so the unsaved
        // changes guard still gets its say before the screen closes.
        router.navigate({ to: origin ?? fallback } as never)
      }}
    >
      <X size={14} aria-hidden="true" />
      Close settings
    </button>
  )
}
